// src/components/popups/TipListDrawer.js

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';

import './TipListDrawer.css';
import { Link } from 'react-router-dom';
import placeholderProfile from '../../assets/placeholder-profile.png';
import api from '../../api';
import ReadMoreText from '../../common/ReadMoreText';
import TipListSkeleton from '../skeleton/TipListSkeleton';

export default function TipListDrawer({
  onClose,
  tips = [],
  currentUser,
  disableHistoryTrap = false,
}) {
  const [localTips, setLocalTips] = useState([]);
  const [loading, setLoading] = useState(true);

  const totalPi = useMemo(
    () => tips.reduce((sum, t) => sum + (Number(t.amount) || 0), 0),
    [tips]
  );

  // --- History trap flags ---
  const pushedRef = useRef(false);
  const closingFromButtonRef = useRef(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // --- Stable close ---
  const safeClose = useCallback(
    ({ manual }) => {
      if (manual) closingFromButtonRef.current = true;

      try {
        onCloseRef.current?.();
      } catch {}

      if (
        manual &&
        !disableHistoryTrap &&
        pushedRef.current &&
        window.history.state?.bfDrawer === 'tiplist'
      ) {
        try {
          window.history.back();
        } catch {}
        pushedRef.current = false;
      }
    },
    [disableHistoryTrap]
  );

  // --- Backdrop click (safe) ---
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target !== e.currentTarget) return;
      safeClose({ manual: true });
    },
    [safeClose]
  );

  // --- Prevent inner clicks bubbling ---
  const stopDrawerPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // --- History + body lock ---
  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;

    body.style.overflow = 'hidden';
    body.classList.add('bf-tip-list-drawer-open');

    if (disableHistoryTrap) {
      return () => {
        body.style.overflow = prevOverflow;
        body.classList.remove('bf-tip-list-drawer-open');
      };
    }

    try {
      window.history.pushState({ bfDrawer: 'tiplist' }, '');
      pushedRef.current = true;
    } catch {}

    const onPopState = () => {
      if (closingFromButtonRef.current) {
        closingFromButtonRef.current = false;
        return;
      }

      safeClose({ manual: false });
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      body.style.overflow = prevOverflow;
      body.classList.remove('bf-tip-list-drawer-open');

      window.removeEventListener('popstate', onPopState);

      if (
        pushedRef.current &&
        window.history.state?.bfDrawer === 'tiplist'
      ) {
        try {
          window.history.back();
        } catch {}
        pushedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableHistoryTrap]);

  // --- Enrich tips with profile pics ---
  useEffect(() => {
    const enrichTips = async () => {
      try {
        const enriched = await Promise.all(
          tips.map(async (tip) => {
            try {
              const res = await api.get(`/users/${tip.tipper}`);
              return {
                ...tip,
                profilePic:
                  res.data?.profilePic || placeholderProfile,
              };
            } catch {
              return {
                ...tip,
                profilePic: placeholderProfile,
              };
            }
          })
        );

        setLocalTips(enriched);
      } catch (err) {
        console.error('❌ Failed to enrich tips:', err);
        setLocalTips(tips);
      } finally {
        setLoading(false);
      }
    };

    enrichTips();
  }, [tips]);

  // =========================
  // Dynamic Drawer Height
  // =========================

const drawerSizeClass =
  tips.length >= 6
    ? 'tip-drawer-large'
    : tips.length <= 2
    ? 'tip-drawer-compact'
    : 'tip-drawer-medium';


return createPortal(

    <div
      className="tip-drawer-backdrop"
      onClick={handleBackdropClick}
    >
      <div
        className={`tip-drawer-content ${drawerSizeClass}`}
        onClick={stopDrawerPropagation}
        onMouseDown={stopDrawerPropagation}
        onTouchStart={stopDrawerPropagation}
      >
        <div className="tip-header">
          <span>Tips</span>

          <div className="tip-head-right">
            <span className="tip-total">
              = {totalPi.toFixed(2)} Pi
            </span>

            <button
              className="tip-close"
              onClick={() => safeClose({ manual: true })}
            >
              ✕
            </button>
          </div>
        </div>

        <div className="tip-scroll">
          {loading ? (

<TipListSkeleton count={Math.min(Math.max(tips.length, 1), 4)} />
          ) : localTips.length === 0 ? (
            <p className="no-tips">No tips yet.</p>
          ) : (
            localTips.map((tip) => (
              <div key={tip._id} className="tip-entry">
                <img
                  src={
                    tip.profilePic?.startsWith('http')
                      ? tip.profilePic
                      : placeholderProfile
                  }
                  alt="tip avatar"
                  className="tip-avatar"
                />

                <div className="tip-info">
                  <Link
                    to={`/user/${tip.tipper}`}
                    className="tip-username"
                  >
                    {tip.tipper}
                  </Link>

                  {tip.message && (
                    <p className="tip-message">
                      <ReadMoreText
                        text={tip.message}
                        maxLines={3}
                        inline
                      />
                    </p>
                  )}

                  <div className="tip-amount">
                    + {Number(tip.amount || 0).toFixed(2)} Pi
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

    </div>,
    document.body
  );
}
